import React, {createRef, CSSProperties, DOMAttributes, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import DocumentSettings from "../../DocumentSettings";
import {ViewerMode} from "../../../viewer/ViewerMode";
import LengthUnit from "../../../../shared/units/LengthUnit";
import Parameter from "../../../dialog/parameter/Parameter";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDialogType from "../../../dialog/PortalDialogType";
import PortalDocumentManager from "../../PortalDocumentManager";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import {TextSearchGlobals} from "./TextSearch";
import DomHelper from "../../../../shared/helper/DomHelper";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import PdfViewer, {PdfViewerDefinition} from "../../../viewer/pdf/PdfViewer";
import PageSelection from "../../PageSelection";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import MenuButton from "../../../../shared/menu/MenuButton";
import {
	Attachment,
	BaseToolbox,
	Settings,
	ToolboxDeleteDelete,
	ToolboxMoveMove,
	ToolboxRotateRotate,
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../../shared/immutable/Immutable";
import {SidebarFunctionGlobals} from "./SidebarFunctions";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {PortalGlobals} from "../../../Portal";
import {DragEventType, PortalDragEvent} from "../../../../shared/interface/PortalDragEvent";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface PageListItemProperties extends TextSearchGlobals, I18nGlobals, SidebarFunctionGlobals, DebugGlobals,
	PortalGlobals, DOMAttributes<HTMLDivElement> {
	previewSize: number;
	currentPage: number;
	active: boolean;
	selected: boolean;
	style?: CSSProperties;
	viewerElement: RefObject<HTMLDivElement>;
	portalDocument: Immutable<PortalDocumentInterface>;
	navigateToDestination?: (dest: string | Array<any> | null) => Promise<void>;
	attachmentKey?: string;
}

interface PageListItemState {
	numberOfPages: number;
	dragOver: boolean;
}

export class PageListItemDefinition extends PortalPureComponent<PageListItemProperties, PageListItemState> {
	private viewerElement: RefObject<HTMLDivElement>;

	/**
	 * Constructs a {@link PageListItem} component
	 */
	constructor(props: PageListItemProperties) {
		super(props);

		this.viewerElement = createRef<HTMLDivElement>();

		this.state = {
			numberOfPages: 0,
			dragOver: false
		}

		this.onSelect = this.onSelect.bind(this);
		this.onDragEnter = this.onDragEnter.bind(this);
		this.onDragOver = this.onDragOver.bind(this);
		this.onDragLeave = this.onDragLeave.bind(this);
		this.onDrop = this.onDrop.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		window.addEventListener('dragend', this.onDragEnd);
		await this.setNumberOfPages();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		window.removeEventListener('dragend', this.onDragEnd);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PageListItemProperties>, prevState: Readonly<PageListItemState>
	): Promise<void> {
		await this.setNumberOfPages();
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let numberOfPages: number = 0;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	/**
	 * called on selecting a page
	 */
	private onSelect(pageNumber: number): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		if (viewerSettings.pageSelection.isPageSelected(pageNumber)) {
			viewerSettings.pageSelection.unselectPage(pageNumber);
		} else {
			viewerSettings.pageSelection.selectPage(pageNumber, this.state.numberOfPages);
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private onDragEnter(event: React.DragEvent): void {
		let dragEvent: PortalDragEvent | undefined = this.props.dragEvent?.get();
		if (dragEvent?.type !== DragEventType.PAGELIST) {
			return;
		}

		let pageSelection: PageSelection = PageSelection.fromString(
			dragEvent!.data, this.state.numberOfPages
		);

		if (!pageSelection.isPageSelected(this.props.currentPage)) {
			this.setState({
				dragOver: true
			});
		}
	}

	private onDragOver(event: React.DragEvent): void {
		let dragEvent: PortalDragEvent | undefined = this.props.dragEvent?.get();
		if (dragEvent?.type !== DragEventType.PAGELIST) {
			return;
		}

		let pageSelection: PageSelection = PageSelection.fromString(
			dragEvent!.data, this.state.numberOfPages
		);

		if (!pageSelection.isPageSelected(this.props.currentPage)) {
			event.preventDefault();
		}
	}

	private onDragLeave(event: React.DragEvent): void {
		let dragEvent: PortalDragEvent | undefined = this.props.dragEvent?.get();
		if (dragEvent?.type !== DragEventType.PAGELIST) {
			return;
		}

		let pageSelection: PageSelection = PageSelection.fromString(
			dragEvent!.data, this.state.numberOfPages
		);

		if (!pageSelection.isPageSelected(this.props.currentPage)) {
			this.setState({
				dragOver: false
			});
		}
	}

	private async onDrop(event: React.DragEvent): Promise<void> {
		let dragEvent: PortalDragEvent | undefined = this.props.dragEvent?.get();
		if (dragEvent?.type !== DragEventType.PAGELIST) {
			return;
		}

		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.onDrop(event);
				}
			});

			return;
		}

		let pageSelection: PageSelection = PageSelection.fromString(
			dragEvent!.data, this.state.numberOfPages
		);

		if (!pageSelection.isPageSelected(this.props.currentPage)) {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);
			let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
			viewerSettings.pageSelection.clearPageSelection();

			let moveParameter: Parameter<ToolboxMoveMove> = DialogDefault.getParameter(
				PortalDialogType.MOVE, [portalDocument]
			);
			moveParameter.data.pages = pageSelection.toString(this.state.numberOfPages, this.props.currentPage);
			moveParameter.data.targetPosition = this.props.currentPage;

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				move: moveParameter.data
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.onDrop(event);
				}
				return;
			}

			let message: Message = new Message("success.toolbox.move", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		event.preventDefault();
		event.stopPropagation();
	}

	private onDragEnd(event: DragEvent): void {
		this.setState({
			dragOver: false
		});
	}

	private checkPasswords(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError())
		) {
			return true;
		}

		return false;
	}

	private async deletePages(deleteParameter: Parameter<ToolboxDeleteDelete>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.deletePages(deleteParameter);
				}
			});

			return;
		}

		DialogDefault.setParameter(
			PortalDialogType.DELETE_PAGE, deleteParameter, this.props.context.documentManager.getSelection()
		);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			delete: deleteParameter.data
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.deletePages(deleteParameter);
				}
				return;
			}

			let message: Message = new Message("success.toolbox.page.delete", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}
	}

	private async rotatePages(rotateParameter: Parameter<ToolboxRotateRotate>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.rotatePages(rotateParameter);
				}
			});

			return;
		}

		DialogDefault.setParameter(
			PortalDialogType.ROTATE, rotateParameter, this.props.context.documentManager.getSelection()
		);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			rotate: rotateParameter.data
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.rotatePages(rotateParameter);
				}
				return;
			}

			let message: Message = new Message("success.toolbox.rotate", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let isPortfolioFile: boolean = portalDocument.isPortfolioFile();
		let rotateParameter: Parameter<ToolboxRotateRotate> = DialogDefault.getParameter(
			PortalDialogType.ROTATE, [portalDocument]
		);
		rotateParameter.data.pages = this.props.currentPage.toString();

		return (
			<div key={this.props.currentPage} style={this.props.style}
				 draggable={
					 !isPortfolioFile &&
					 this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
					 this.props.context.info.functionsStatus?.movePages
				 }
				 className={
					 "page-item" + (this.props.selected ? " selected" : "") + (this.props.active ? " active" : "") +
					 (this.state.dragOver ? " dragover" : "")
				 }
				 onDragStartCapture={this.props.onDragStartCapture} onDragEnterCapture={this.onDragEnter}
				 onDragLeaveCapture={this.onDragLeave} onDropCapture={this.onDrop} onDragOverCapture={this.onDragOver}
				 onClick={(event) => {
					 if (DomHelper.hasClass(event.target as HTMLElement, "functions")) {
						 return;
					 }

					 this.onSelect(this.props.currentPage)
				 }}>
				<div className="column-preview" style={{
					width: this.props.previewSize + LengthUnit.PIXEL,
					height: this.props.previewSize + LengthUnit.PIXEL
				}} onClick={(event) => {
					viewerSettings.activeViewer.currentPage = this.props.currentPage;
					this.props.context.documentManager.updateGlobalDocumentList();
				}}>
					{isPortfolioFile &&
                        <>
                            <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props}
                                       attachmentKey={this.props.attachmentKey} viewerMode={ViewerMode.PORTFOLIO_PAGE}/>
                        </>
					}
					{!isPortfolioFile &&
                        <>
                            <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props}
                                       viewerMode={ViewerMode.PAGE}/>
                            <div className="functions">
                                <MenuButton {...this.props} className="button-rotate-270"
                                            icon="icon icon-rotate-270 small"
                                            onClick={async (event) => {
												event.stopPropagation();

												let command: () => Promise<void> = async () => {
													rotateParameter.data.degrees = 270;
													await this.rotatePages(rotateParameter);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
                                <MenuButton {...this.props} className="button-rotate-90"
                                            icon="icon icon-rotate-90 small"
                                            onClick={async (event) => {
												event.stopPropagation();

												let command: () => Promise<void> = async () => {
													rotateParameter.data.degrees = 90;
													await this.rotatePages(rotateParameter);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
                                <MenuButton {...this.props} className="button-delete" icon="icon icon-delete small"
                                            onClick={async (event) => {
												event.stopPropagation();

												let command: () => Promise<void> = async () => {
													let deletePageParameter: Parameter<ToolboxDeleteDelete> =
														DialogDefault.getParameter(
															PortalDialogType.DELETE_PAGE, [portalDocument]
														);
													deletePageParameter.data.pages = this.props.currentPage.toString();
													await this.deletePages(deletePageParameter);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
                            </div>
                        </>
					}
				</div>
				<div className="column-page">{this.props.currentPage}</div>
			</div>
		);
	}
}

const PageListItem = React.memo(PageListItemDefinition,
	(prevProps: Readonly<PageListItemProperties>, nextProps: Readonly<PageListItemProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			if (nextProps.portalDocument.get().id !== prevProps.portalDocument.get().id) {
				return false;
			}

			if (nextProps.portalDocument.get().updated !== prevProps.portalDocument.get().updated) {
				return false;
			}
		}

		if (FunctionHelper.exists(nextProps.dragEvent) && !nextProps.dragEvent!.equals(prevProps.dragEvent)) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.previewSize !== prevProps.previewSize) {
			return false;
		}

		if (nextProps.selected !== prevProps.selected) {
			return false;
		}

		if (nextProps.active !== prevProps.active) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default PageListItem;